import {
  findHardwareAnchor,
  findHardwareZone,
  PRODUCT_ATTACHMENT_ANCHOR_ID,
  type HardwareAnchor,
  type HardwareGuides,
  type HardwareZone,
} from '../assets/hardwareGuides';
import type { HardwareProduct, HardwareRenderSize } from './hardwareCatalog';
import type { PreparedHardwareArtwork } from './prepareHardwareArtwork';

export const STANDARD_HANDLE_PLACEMENT_ID = 'standard-anchor';
export const HERITAGE_HANDLE_PLACEMENT_ID = 'heritage-anchor';
export const PULL_BAR_PLACEMENT_ID = 'pullbar-anchor';

/** Slab lock points a bar escutcheon may cycle through. Finger-pulls stay one product per position. */
export function escutcheonPlacementIds(availability: {
  standardHandle: boolean;
  heritage: boolean;
}): string[] {
  const ids: string[] = [];
  if (availability.standardHandle) ids.push(STANDARD_HANDLE_PLACEMENT_ID);
  if (availability.heritage) ids.push(HERITAGE_HANDLE_PLACEMENT_ID);
  return ids;
}

/** Named `#hardware-guides` id this product attaches to. Coordinates stay on the slab. */
export function slabPlacementIdForProduct(
  product: Pick<HardwareProduct, 'family' | 'position'> & { role?: HardwareProduct['role'] }
): string | null {
  switch (product.family) {
    case 'pull-bar':
      return PULL_BAR_PLACEMENT_ID;
    case 'lever':
      return product.position === 'heritage' ? HERITAGE_HANDLE_PLACEMENT_ID : STANDARD_HANDLE_PLACEMENT_ID;
    case 'locking':
      if (product.role === 'finger-pull-escutcheon' || product.role === 'escutcheon') {
        return product.position === 'heritage' ? HERITAGE_HANDLE_PLACEMENT_ID : STANDARD_HANDLE_PLACEMENT_ID;
      }
      return null;
    case 'knocker':
      return 'knocker-anchor';
    case 'spyhole':
      return 'spyhole-anchor';
    default:
      return null;
  }
}

export interface HardwareOrigin {
  x: number;
  y: number;
  source: 'product-anchor' | 'viewBox-centre';
}

export interface PlacedHardware {
  productId: string;
  placementId: string;
  publicPath: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PlaceableHardwareProduct {
  id: string;
  publicPath: string;
  renderSize?: HardwareRenderSize;
}

/**
 * Attachment point in the hardware SVG's own (viewBox) space.
 * Prefer the product's authored `anchor-point`. Legacy `standard-anchor` /
 * spindle-centre still work. ViewBox centre is only the fallback.
 */
export function hardwareOrigin(artwork: PreparedHardwareArtwork): HardwareOrigin {
  const attachment =
    findHardwareAnchor(artwork.hardwareGuides, PRODUCT_ATTACHMENT_ANCHOR_ID) ??
    findHardwareAnchor(artwork.hardwareGuides, STANDARD_HANDLE_PLACEMENT_ID) ??
    findHardwareAnchor(artwork.hardwareGuides, 'letterplate-anchor') ??
    artwork.hardwareGuides.anchors.find(
      (anchor) => anchor.anchorType === 'attachment' || anchor.anchorType === 'spindle-centre'
    );
  if (attachment) {
    return { x: attachment.x, y: attachment.y, source: 'product-anchor' };
  }
  return {
    x: artwork.width / 2,
    y: artwork.height / 2,
    source: 'viewBox-centre',
  };
}

/**
 * Catalogue size in slab space. ViewBox numbers are only used when the product
 * has not declared a render size.
 */
export function hardwareRenderSize(
  artwork: PreparedHardwareArtwork,
  product: PlaceableHardwareProduct
): HardwareRenderSize {
  const width = product.renderSize?.width;
  const height = product.renderSize?.height;
  if (
    typeof width === 'number' &&
    width > 0 &&
    typeof height === 'number' &&
    height > 0
  ) {
    return { width, height };
  }
  return { width: artwork.width, height: artwork.height };
}

function scaleOrigin(
  origin: HardwareOrigin,
  artwork: PreparedHardwareArtwork,
  size: HardwareRenderSize
): { x: number; y: number } {
  return {
    x: origin.x * (size.width / artwork.width),
    y: origin.y * (size.height / artwork.height),
  };
}

export function placeHardwareOnAnchor(
  anchor: HardwareAnchor,
  artwork: PreparedHardwareArtwork,
  product: PlaceableHardwareProduct
): PlacedHardware {
  const origin = hardwareOrigin(artwork);
  const size = hardwareRenderSize(artwork, product);
  const scaledOrigin = scaleOrigin(origin, artwork, size);
  return {
    productId: product.id,
    placementId: anchor.id,
    publicPath: artwork.href ?? product.publicPath,
    x: anchor.x - scaledOrigin.x,
    y: anchor.y - scaledOrigin.y,
    width: size.width,
    height: size.height,
  };
}

/** Slab attachment for a footprint zone: product origin sits on the zone centre. */
export function zoneCentreAnchor(zone: HardwareZone): HardwareAnchor {
  return {
    id: zone.id,
    role: zone.role,
    anchorType: zone.zoneType || 'centre',
    position: zone.position,
    x: zone.x + zone.width / 2,
    y: zone.y + zone.height / 2,
  };
}

export function placeHardwareInZone(
  zone: HardwareZone,
  artwork: PreparedHardwareArtwork,
  product: PlaceableHardwareProduct
): PlacedHardware {
  return placeHardwareOnAnchor(zoneCentreAnchor(zone), artwork, product);
}

export function placeHardwareAt(
  slabGuides: HardwareGuides,
  artwork: PreparedHardwareArtwork | null,
  product: PlaceableHardwareProduct | undefined,
  placementId: string
): PlacedHardware | null {
  if (!artwork || !product) return null;
  const zone = findHardwareZone(slabGuides, placementId);
  if (zone) return placeHardwareInZone(zone, artwork, product);
  const anchor = findHardwareAnchor(slabGuides, placementId);
  if (!anchor) return null;
  return placeHardwareOnAnchor(anchor, artwork, product);
}

export function placeStandardHandle(
  slabGuides: HardwareGuides,
  artwork: PreparedHardwareArtwork | null,
  product: PlaceableHardwareProduct | undefined
): PlacedHardware | null {
  return placeHardwareAt(slabGuides, artwork, product, STANDARD_HANDLE_PLACEMENT_ID);
}

export function placePullBar(
  slabGuides: HardwareGuides,
  artwork: PreparedHardwareArtwork | null,
  product: PlaceableHardwareProduct | undefined
): PlacedHardware | null {
  return placeHardwareAt(slabGuides, artwork, product, PULL_BAR_PLACEMENT_ID);
}
